import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, ChevronRight, Compass, Send, Building2,
  Star, Calendar, Gift, ArrowRight, Globe, CheckCircle
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import ConsumerLayout from '@/components/ConsumerLayout';
import { useLocation } from '@/contexts/LocationContext';
import api from '@/lib/api';
import { toast } from 'sonner';

// Neighborhood Card Component
const NeighborhoodCard = ({ neighborhood, onSelect, distance }) => {
  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(neighborhood)}
      className="cursor-pointer"
    >
      <Card className="bg-slate-800 border-slate-700 hover:border-indigo-500/50 transition-all overflow-hidden h-full group">
        <div className="relative h-40 overflow-hidden">
          {neighborhood.image_url ? (
            <img
              src={neighborhood.image_url}
              alt={neighborhood.name}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-indigo-600 to-purple-700 flex items-center justify-center">
              <MapPin className="w-12 h-12 text-white/50" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="font-bold text-white text-lg mb-1">{neighborhood.name}</h3>
            <p className="text-sm text-slate-300">{neighborhood.city}, {neighborhood.state}</p>
          </div>
          {distance !== undefined && (
            <div className="absolute top-3 right-3 bg-slate-900/80 backdrop-blur-sm px-2 py-1 rounded text-xs text-slate-300">
              {distance} mi away
            </div>
          )}
        </div>
        <CardContent className="p-4">
          <p className="text-sm text-slate-400 line-clamp-2 mb-3">
            {neighborhood.short_description || neighborhood.description}
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-xs text-slate-500">
              {neighborhood.stats?.venues > 0 && (
                <span className="flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  {neighborhood.stats.venues}
                </span>
              )}
              {neighborhood.stats?.events > 0 && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {neighborhood.stats.events}
                </span>
              )}
            </div>
            <span className="text-indigo-400 text-sm font-medium group-hover:text-indigo-300 flex items-center gap-1">
              Explore <ChevronRight className="w-4 h-4" />
            </span>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const LocationPicker = () => {
  const navigate = useNavigate();
  const { 
    allNeighborhoods, 
    nearbyNeighborhoods, 
    selectNeighborhood,
    isInSupportedArea,
    fetchNeighborhoods
  } = useLocation();
  
  const [showSuggestionDialog, setShowSuggestionDialog] = useState(false);
  const [suggestionForm, setSuggestionForm] = useState({
    venue_name: '',
    venue_type: '',
    address: '',
    contact_info: '',
    suggested_by_email: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchNeighborhoods();
  }, [fetchNeighborhoods]);

  const handleSelectNeighborhood = (neighborhood) => {
    selectNeighborhood(neighborhood);
    navigate('/');
  };

  const handleSubmitSuggestion = async (e) => {
    e.preventDefault();
    if (!suggestionForm.venue_name || !suggestionForm.venue_type) {
      toast.error('Please fill in venue name and type');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/neighborhoods/suggestions', suggestionForm);
      setSubmitted(true);
      toast.success('Thank you for your suggestion!');
    } catch (error) {
      toast.error('Failed to submit suggestion');
    } finally {
      setSubmitting(false);
    }
  };

  const displayNeighborhoods = nearbyNeighborhoods.length > 0 
    ? nearbyNeighborhoods 
    : allNeighborhoods;

  return (
    <ConsumerLayout darkTheme>
      
      {/* Hero Section */}
      <section className="relative py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-indigo-900/30 to-[#0f172a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {!isInSupportedArea ? (
              <>
                <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
                  <Globe className="w-10 h-10 text-amber-400" />
                </div>
                <h1 className="headline-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-50 mb-4">
                  We're Expanding to Your Area <span className="text-amber-400">Soon!</span>
                </h1>
                <p className="text-lg sm:text-xl text-slate-300 mb-6 max-w-2xl mx-auto">
                  EVENT★GOERS isn't in your neighborhood yet, but we're growing fast. 
                  Explore our current locations or help us expand by suggesting venues!
                </p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 rounded-full bg-indigo-500/20 flex items-center justify-center mx-auto mb-6">
                  <MapPin className="w-10 h-10 text-indigo-400" />
                </div>
                <h1 className="headline-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-50 mb-4">
                  Choose Your <span className="text-indigo-400">Neighborhood</span>
                </h1>
                <p className="text-lg sm:text-xl text-slate-300 mb-6 max-w-2xl mx-auto">
                  Select your neighborhood to discover the best events, venues, and experiences near you.
                </p>
              </>
            )}
          </motion.div>
        </div>
      </section>

      {/* Neighborhoods Grid */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="headline-serif text-xl sm:text-2xl font-bold text-slate-50 mb-2">
              {nearbyNeighborhoods.length > 0 ? 'Nearby Neighborhoods' : 'Available Neighborhoods'}
            </h2>
            <p className="text-slate-400 text-sm sm:text-base">
              {nearbyNeighborhoods.length > 0 
                ? 'Check out these neighborhoods close to you'
                : 'Explore events and experiences in these Denver areas'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {displayNeighborhoods.map((neighborhood) => (
              <NeighborhoodCard
                key={neighborhood.id}
                neighborhood={neighborhood}
                onSelect={handleSelectNeighborhood}
                distance={neighborhood.distance_miles}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Plan a Trip CTA */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-gradient-to-br from-indigo-900/50 to-purple-900/50 border-indigo-500/30 h-full">
                <CardContent className="p-6 sm:p-8">
                  <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center mb-4">
                    <Compass className="w-6 h-6 text-indigo-400" />
                  </div>
                  <h3 className="headline-serif text-xl sm:text-2xl font-bold text-white mb-3">
                    Plan Your Visit
                  </h3>
                  <p className="text-indigo-200/80 mb-6 text-sm sm:text-base">
                    Let Evis, our AI concierge, create a personalized itinerary for your trip to Denver.
                  </p>
                  <Button
                    onClick={() => navigate('/guides')}
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                  >
                    Ask Evis
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Suggest a Venue CTA */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-gradient-to-br from-amber-900/50 to-orange-900/50 border-amber-500/30 h-full">
                <CardContent className="p-6 sm:p-8">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center mb-4">
                    <Building2 className="w-6 h-6 text-amber-400" />
                  </div>
                  <h3 className="headline-serif text-xl sm:text-2xl font-bold text-white mb-3">
                    Help Us Expand
                  </h3>
                  <p className="text-amber-200/80 mb-6 text-sm sm:text-base">
                    Know a great venue we should partner with? Let us know and we'll reach out to them!
                  </p>
                  <Button
                    onClick={() => setShowSuggestionDialog(true)}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-black"
                  >
                    Suggest a Venue
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Venue Suggestion Dialog */}
      <Dialog open={showSuggestionDialog} onOpenChange={setShowSuggestionDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-50 max-w-lg">
          <DialogHeader>
            <DialogTitle className="headline-serif text-2xl">Suggest a Venue</DialogTitle>
            <DialogDescription className="text-slate-400">
              Help us discover amazing places to partner with. We'll review your suggestion and reach out to them!
            </DialogDescription>
          </DialogHeader>
          
          {submitted ? (
            <div className="py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Thank You!</h3>
              <p className="text-slate-400 mb-6">
                We've received your suggestion and will review it soon.
              </p>
              <Button
                onClick={() => {
                  setShowSuggestionDialog(false);
                  setSubmitted(false);
                  setSuggestionForm({
                    venue_name: '',
                    venue_type: '',
                    address: '',
                    contact_info: '',
                    suggested_by_email: '',
                    notes: ''
                  });
                }}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmitSuggestion} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1 space-y-2">
                  <Label className="text-slate-300">Venue Name *</Label>
                  <Input
                    placeholder="e.g., The Blue Moon"
                    value={suggestionForm.venue_name}
                    onChange={(e) => setSuggestionForm({...suggestionForm, venue_name: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-slate-50"
                    required
                  />
                </div>
                <div className="col-span-2 sm:col-span-1 space-y-2">
                  <Label className="text-slate-300">Venue Type *</Label>
                  <Select 
                    value={suggestionForm.venue_type} 
                    onValueChange={(value) => setSuggestionForm({...suggestionForm, venue_type: value})}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-50">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="restaurant">Restaurant</SelectItem>
                      <SelectItem value="bar">Bar / Lounge</SelectItem>
                      <SelectItem value="brewery">Brewery / Taproom</SelectItem>
                      <SelectItem value="club">Club / Nightlife</SelectItem>
                      <SelectItem value="gallery">Art Gallery</SelectItem>
                      <SelectItem value="music_venue">Music Venue</SelectItem>
                      <SelectItem value="cafe">Cafe / Coffee</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Address (if known)</Label>
                <Input
                  placeholder="e.g., 123 Main St, Denver"
                  value={suggestionForm.address}
                  onChange={(e) => setSuggestionForm({...suggestionForm, address: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-slate-50"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Contact Info (if known)</Label>
                <Input
                  placeholder="Phone, email, or website"
                  value={suggestionForm.contact_info}
                  onChange={(e) => setSuggestionForm({...suggestionForm, contact_info: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-slate-50"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Your Email (optional)</Label>
                <Input
                  type="email"
                  placeholder="We'll let you know if we partner with them"
                  value={suggestionForm.suggested_by_email}
                  onChange={(e) => setSuggestionForm({...suggestionForm, suggested_by_email: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-slate-50"
                />
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowSuggestionDialog(false)}
                  className="border-slate-600 text-slate-300"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                >
                  {submitting ? 'Submitting...' : 'Submit Suggestion'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

    </ConsumerLayout>
  );
};

export default LocationPicker;
